import automationApi from './automationApi';
import api from './api';

/**
 * Search for creators/content matching the given filters.
 * `filters.platform`: 'youtube' (default) or 'instagram'.
 * YouTube keys: keyword, categoryId, order, regionCode, publishedWithinDays,
 * minViews, minComments, maxComments, maxResults.
 * Instagram keys: keyword, minViews/maxViews (poster's estimated followers —
 * Instagram has no comment/like data available via Serper), maxResults.
 */
export const searchCreators = async (filters = {}) => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== null && v !== undefined)
  );
  const response = await automationApi.get('/account-automation/search', { params });
  return response.data;
};

/**
 * Get today's estimated YouTube API quota usage.
 */
export const getQuota = async () => {
  const response = await automationApi.get('/account-automation/quota');
  return response.data;
};

/**
 * Find candidate emails for a creator by scraping their website + Hunter.io.
 */
export const findEmail = async (payload) => {
  const response = await automationApi.post('/account-automation/find-email', payload);
  return response.data;
};

/**
 * Create a pending affiliate account for a discovered creator.
 */
export const createPendingAccount = async (payload) => {
  const response = await automationApi.post('/account-automation/pending-accounts', payload);
  return response.data;
};

/**
 * Poll the video-download-to-S3 status for a blog created via createPendingAccount.
 * status is 'pending' | 'success' | 'failed'.
 */
export const getBlogVideoStatus = async (blogId) => {
  const response = await automationApi.get(`/account-automation/blog-status/${blogId}`);
  return response.data;
};

/**
 * Offers, scoped to the production database — Account Automation always runs
 * against production (see automationApi.js), so the offerId/categoryId sent
 * with createPendingAccount must come from production too, not whichever
 * backend the rest of this client build happens to be pointed at.
 */
export const getOffersForAutomation = async (page = 1, limit = 100) => {
  const response = await automationApi.get(`/offers?page=${page}&limit=${limit}`);
  return response.data;
};

/**
 * Categories, scoped to the production database — see getOffersForAutomation.
 */
export const getCategoriesForAutomation = async () => {
  const response = await automationApi.get('/categories/select');
  return response.data;
};

/**
 * Run the yt-dlp download + S3 upload against whichever backend THIS client
 * build/dev-server is normally pointed at (the shared `api` instance, not
 * `automationApi`) — i.e. your local machine, not production. Production's
 * outbound IP is a datacenter IP that YouTube frequently bot-checks/blocks;
 * a locally-run backend doesn't have that problem. Both backends read/write
 * the same S3 bucket (see server/.env.*), so the uploaded file is already
 * exactly where production needs it — no separate "upload to prod" step.
 * Pairs with attachVideoToProdBlog/markProdBlogVideoFailed below.
 */
export const downloadVideoLocally = async ({ videoId, url }) => {
  const response = await api.post('/account-automation/download-only', { videoId, url });
  return response.data; // { s3Url }
};

/**
 * Attach a locally-downloaded (and already S3-uploaded) video to a blog that
 * lives on production, and flip its videoDownloadStatus to 'success'.
 */
export const attachVideoToProdBlog = async (blogId, s3Url) => {
  const response = await automationApi.post(`/account-automation/blog-video/${blogId}`, { s3Url });
  return response.data;
};

/**
 * Mark a production blog's video download as failed (e.g. the local download
 * step itself errored out) so the admin UI stops showing "Processing…".
 */
export const markProdBlogVideoFailed = async (blogId, error) => {
  const response = await automationApi.post(`/account-automation/blog-video/${blogId}`, { error });
  return response.data;
};

export default {
  searchCreators,
  getQuota,
  findEmail,
  createPendingAccount,
  getBlogVideoStatus,
  getOffersForAutomation,
  getCategoriesForAutomation,
  downloadVideoLocally,
  attachVideoToProdBlog,
  markProdBlogVideoFailed
};
